import { useEffect, useRef } from "react";

/**
 * useAutoRefresh — refresca datos en background cada `intervalMs` ms.
 *
 * Parámetros:
 *   callback   — función async que hace el fetch (igual al que usás en useEffect inicial)
 *   intervalMs — intervalo en ms (por defecto 30 000 = 30 seg)
 *   enabled    — si es false, pausa el intervalo (útil cuando hay modales abiertos)
 *
 * Uso típico en una page:
 *
 *   import { useAutoRefresh } from "../hooks/useAutoRefresh";
 *
 *   // En el componente:
 *   const modalAbierto = !!modalVisible;
 *   useAutoRefresh(fetchDatos, 30_000, !modalAbierto);
 *
 * El callback se estabiliza internamente con useRef para evitar re-registros
 * innecesarios cuando cambia por cierres sobre deps que rotan (token, etc.).
 * Pasá una función que ya esté memoizada con useCallback, o una arrow inline
 * que capture las deps que necesite — el hook sólo re-programa el intervalo
 * cuando cambia `enabled` o `intervalMs`.
 */
export function useAutoRefresh(callback, intervalMs = 30_000, enabled = true) {
  const callbackRef = useRef(callback);

  // Mantener la referencia actualizada sin reprogramar el intervalo
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => {
      callbackRef.current?.();
    }, intervalMs);
    return () => clearInterval(id);
  }, [enabled, intervalMs]);
}
